import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  HrUser,
  Leave,
  CreateUserPayload,
  UpdateUserPayload,
  SubmitLeavePayload,
  LeaveStatus,
} from '../types';

// ─── Users (Team & Access) ────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data } = await api.get<{ users: HrUser[] }>('/api/admin/users');
      return data.users ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useUserMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-users'] });

  const createUser = useMutation({
    mutationFn: async (payload: CreateUserPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/admin/users', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateUser = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateUserPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/admin/users/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  /** Promote/demote an existing user. Backend: superadmin only, 'admin' | 'employee'. */
  const setUserRole = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: 'admin' | 'employee' }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/admin/users/${id}/role`, { role });
      return data;
    },
    onSuccess: invalidate,
  });

  return { createUser, updateUser, setUserRole };
}

// ─── Leaves ──────────────────────────────────────────────────────────────

export function useLeaves() {
  return useQuery({
    queryKey: ['leaves'],
    queryFn: async () => {
      const { data } = await api.get<{ leaves: Leave[] }>('/api/leaves');
      return data.leaves ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useLeaveMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['leaves'] });
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const submitLeave = useMutation({
    mutationFn: async (payload: SubmitLeavePayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/leaves', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const reviewLeave = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Extract<LeaveStatus, 'approved' | 'rejected'> }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/leaves/${id}`, { status });
      return data;
    },
    onSuccess: invalidate,
  });

  return { submitLeave, reviewLeave };
}
